import { useMemo, useState } from 'react'

import { usePriorityProjects } from '@/features/priorities/api'
import type { AppConnected, AppStatus } from '@/shared/api/contracts'
import { CopyButton } from '@/shared/components/common'
import { Button, Field, Input, Select, Sheet, Textarea } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useConnectSupportApp } from '../api'
import { envSnippet, slugDe } from '../snippets'
import { ActivationGuide } from './ActivationGuide'
import { ManualForm } from './ManualForm'

type Paso = 'datos' | 'clave' | 'manual' | 'activacion'
const PASOS: readonly Paso[] = ['datos', 'clave', 'manual', 'activacion']

export interface ConnectAppSheetProps {
  open: boolean
  onClose: () => void
}

/**
 * "Conectar app": el alta de una app nueva en el chat de soporte, de punta a punta.
 *
 * 1. **Datos**: nombre, slug, los dominios desde los que carga el widget y el
 *    proyecto de la app en el PM. Se crea su "Soporte - <App>" vinculado a él.
 * 2. **Clave**: la `ssk_…`, que se ve UNA vez. Va como `SIETE_SOPORTE_KEY` en
 *    el backend de la app.
 * 3. **Manual**: si siete-soporte no tiene el manual técnico, se pide acá, con
 *    la estructura que necesita la IA. Sin manual no se sigue.
 * 4. **Activación**: el tour de instalación, que termina solo en "Conectada"
 *    cuando la app pide su primer pase.
 */
export function ConnectAppSheet({ open, onClose }: ConnectAppSheetProps) {
  const t = useT()
  const [paso, setPaso] = useState<Paso>('datos')
  const [conectada, setConectada] = useState<AppConnected | null>(null)
  const [app, setApp] = useState<AppStatus | null>(null)

  const cerrar = () => {
    setPaso('datos')
    setConectada(null)
    setApp(null)
    onClose()
  }

  const nombreDelPaso: Record<Paso, string> = {
    datos: t('support.apps.connect.steps.datos'),
    clave: t('support.apps.connect.steps.clave'),
    manual: t('support.apps.connect.steps.manual'),
    activacion: t('support.apps.connect.steps.activacion'),
  }

  const titulo = (
    <div className="flex flex-col gap-0.5">
      <span className="font-display text-sm text-text-1">
        {app
          ? t('support.apps.connect.titleOf', { app: app.name })
          : t('support.apps.connect.title')}
      </span>
      <span className="text-2xs text-text-3">
        {t('support.apps.connect.step', {
          actual: PASOS.indexOf(paso) + 1,
          total: PASOS.length,
          paso: nombreDelPaso[paso],
        })}
      </span>
    </div>
  )

  return (
    <Sheet open={open} onClose={cerrar} title={titulo} className="max-w-[640px]">
      {paso === 'datos' ? (
        <DatosForm
          onConnected={(respuesta) => {
            setConectada(respuesta)
            setApp(respuesta.app)
            setPaso('clave')
          }}
        />
      ) : null}

      {paso === 'clave' && conectada ? (
        <div className="flex flex-col gap-3 text-xs text-text-2">
          <p>{t('support.apps.key.once', { app: conectada.app.name })}</p>
          <div role="status" className="relative">
            <pre className="num overflow-x-auto rounded-[var(--radius-control)] bg-bg p-2 pr-8 text-2xs text-text-1 select-all">
              {envSnippet(conectada.key)}
            </pre>
            <CopyButton
              value={envSnippet(conectada.key)}
              label={t('support.apps.key.copy')}
              className="absolute top-1 right-1"
            />
          </div>
          <p className="text-text-3">{t('support.apps.key.where')}</p>
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={() => {
                // La clave se olvida acá: de ahora en más sólo se ve su prefijo.
                setConectada(null)
                setPaso(app?.manual ? 'activacion' : 'manual')
              }}
            >
              {t('support.apps.key.saved')}
            </Button>
          </div>
        </div>
      ) : null}

      {paso === 'manual' && app ? (
        <ManualForm
          app={app}
          required
          onSaved={(estado) => {
            setApp(estado)
            setPaso('activacion')
          }}
        />
      ) : null}

      {paso === 'activacion' && app ? (
        <div className="flex flex-col gap-4">
          <ActivationGuide app={app} />
          <div className="flex justify-end">
            <Button variant="primary" onClick={cerrar}>
              {t('support.apps.connect.done')}
            </Button>
          </div>
        </div>
      ) : null}
    </Sheet>
  )
}

function DatosForm({ onConnected }: { onConnected: (respuesta: AppConnected) => void }) {
  const t = useT()
  const proyectos = usePriorityProjects()
  const conectar = useConnectSupportApp()
  const [nombre, setNombre] = useState('')
  const [slug, setSlug] = useState<string | null>(null)
  const [dominios, setDominios] = useState('')
  const [proyectoId, setProyectoId] = useState('')

  const slugFinal = slug ?? slugDe(nombre)
  const origenes = useMemo(
    () =>
      dominios
        .split(/[\s,]+/)
        .map((dominio) => dominio.trim())
        .filter(Boolean),
    [dominios],
  )
  const listo = nombre.trim() && slugFinal.length >= 2 && origenes.length > 0 && proyectoId

  return (
    <form
      aria-label={t('support.apps.connect.title')}
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (!listo) return
        conectar.mutate(
          {
            name: nombre.trim(),
            tool_slug: slugFinal,
            origins: origenes,
            linked_project_id: proyectoId,
          },
          { onSuccess: onConnected },
        )
      }}
    >
      <p className="text-xs text-text-3">{t('support.apps.connect.description')}</p>

      <Field label={t('support.apps.connect.name')} htmlFor="soporte-app-nombre">
        <Input
          id="soporte-app-nombre"
          autoFocus
          value={nombre}
          maxLength={100}
          placeholder="Outply"
          onChange={(event) => setNombre(event.target.value)}
        />
      </Field>

      <Field
        label={t('support.apps.connect.slug')}
        htmlFor="soporte-app-slug"
        hint={t('support.apps.connect.slugHint')}
      >
        <Input
          id="soporte-app-slug"
          value={slugFinal}
          maxLength={50}
          className="num"
          onChange={(event) => setSlug(slugDe(event.target.value))}
        />
      </Field>

      <Field label={t('support.apps.connect.project')} htmlFor="soporte-app-proyecto">
        <Select
          id="soporte-app-proyecto"
          value={proyectoId}
          disabled={proyectos.isPending}
          onChange={(event) => setProyectoId(event.target.value)}
        >
          <option value="">{t('support.apps.connect.projectPlaceholder')}</option>
          {(proyectos.data ?? []).map((proyecto) => (
            <option key={proyecto.id} value={proyecto.id}>
              {proyecto.name} · {proyecto.company.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field
        label={t('support.apps.connect.origins')}
        htmlFor="soporte-app-dominios"
        hint={t('support.apps.connect.originsHint')}
      >
        <Textarea
          id="soporte-app-dominios"
          rows={3}
          value={dominios}
          placeholder="https://outply.wearesiete.com"
          className="num"
          onChange={(event) => setDominios(event.target.value)}
        />
      </Field>

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!listo || conectar.isPending}>
          {t('support.apps.connect.submit')}
        </Button>
      </div>
    </form>
  )
}
